import React, { useEffect, useState } from "react";
import moment from "moment";
import Str from "../../../common/Str";
import Checkbox from "../../../library/Checkbox";
import DataTable from "../../../library/DataTable";
import SkeletonTicketList from "../../../../loaders/SkeletonTicketList";
import { getRowsPerPageOptions } from "../studyPaths/studyPaths.utils";
import ModuleReadinessPopover from "./ModuleReadinessPopover";
import LinkedProgrammeFilters from "./LinkedProgrammeFilters";
import useLinkedProgrammeExport from "./useLinkedProgrammeExport";
import {
    ActionsCell,
    GradeCell,
    ProgrammeDetailsCell,
    ReadinessCell,
    VersionCell,
} from "./LinkedProgrammeRowCells";
import { TABLE_DATE_FORMAT } from "../../../../utils/Constants";

/**
 * Table of linked programmes (standard and QA views), with search/filters/exports, sorting, row
 * selection, grade lock/unlock, a module-readiness popover and pagination - built on the shared
 * `DataTable`, same as the Assign Modules modal.
 * @param {Object} hook State and handlers from the linked-programmes list hook.
 * @param {number|string} id The programme id, used to scope exports.
 * @param {Object} detail Programme detail (e.g. netsuite_product), used by export.
 */
function LinkedProgrammesTable({ hook, id, detail }) {
    const [modulesRow, setModulesRow] = useState(null);
    const [modulesAnchor, setModulesAnchor] = useState(null);
    const [settledTotalRows, setSettledTotalRows] = useState(0);
    const exportOptions = useLinkedProgrammeExport(hook, id, detail);

    // Paging away unmounts the row button the popover is anchored to, so close it.
    useEffect(() => {
        setModulesRow(null);
        setModulesAnchor(null);
    }, [hook.page, hook.perPage]);

    const handleToggleModules = (row, event) => {
        if (modulesRow?.id === row.id) {
            setModulesRow(null);
            setModulesAnchor(null);
            return;
        }

        setModulesRow(row);
        setModulesAnchor(event.currentTarget);
    };

    // totalRows drops to 0 while a page loads, so size the options from the last settled total
    useEffect(() => {
        if (!hook.loading) {
            setSettledTotalRows(hook.totalRows);
        }
    }, [hook.loading, hook.totalRows]);

    const rowsPerPageOptions = Array.from(
        new Set([...getRowsPerPageOptions(settledTotalRows), hook.perPage])
    ).sort((first, second) => first - second);

    const formatDate = (timestamp) => (timestamp ? moment.unix(timestamp).format(TABLE_DATE_FORMAT) : "-");

    const columns = [
        {
            key: "select",
            label: (
                <Checkbox
                    checked={hook.allMatchingSelected}
                    indeterminate={hook.someMatchingSelected}
                    disabled={hook.isProcessing || hook.loading || hook.totalRows === 0}
                    onChange={hook.toggleAllMatching}
                    ariaLabel="Select all programmes matching current filters"
                />
            ),
            sortable: false,
            width: "49px",
            render: (row) => (
                <Checkbox
                    checked={hook.selectedIds.includes(row.id)}
                    disabled={hook.isProcessing}
                    onChange={() => hook.toggleRow(row.id)}
                    ariaLabel={`Select ${row?.name || "programme"}`}
                />
            ),
        },
        {
            key: "name",
            label: "Programme Details",
            render: (row) => <ProgrammeDetailsCell row={row} />,
        },
        hook.isVersionEnabled && {
            key: "version_name",
            label: "Version",
            render: (row) => <VersionCell row={row} programmeId={hook.id} />,
        },
        {
            key: "intakes",
            label: "Intake",
            render: (row) => row?.intakes || "-",
        },
        ...(hook.QAview ? [
            {
                key: "module_ready",
                label: "Module readiness",
                sortable: false,
                render: (row) => (
                    <ReadinessCell
                        row={row}
                        hook={hook}
                        modulesRow={modulesRow}
                        onToggleModules={handleToggleModules}
                    />
                ),
            },
        ] : [
            {
                key: "start",
                label: "Start",
                icon: "calendar-alt",
                render: (row) => formatDate(row?.start),
            },
            {
                key: "end",
                label: "End",
                icon: "calendar-alt",
                render: (row) => formatDate(row?.end),
            },
        ]),
        {
            key: "isGradeLocked",
            label: "Grades",
            render: (row) => <GradeCell row={row} hook={hook} />,
        },
        {
            key: "total_student",
            label: "",
            icon: "users",
            ariaLabel: "Students",
            align: "center",
            render: (row) => row?.total_student ?? "-",
        },
        {
            key: "action",
            label: "Action",
            sortable: false,
            width: "1%",
            render: (row) => <ActionsCell row={row} hook={hook} />,
        },
    ].filter(Boolean);

    return (
        <>
            <DataTable
                columns={columns}
                data={hook.listData}
                keyField="id"
                loading={hook.loading}
                loadingMessage={<SkeletonTicketList />}
                emptyMessage={Str.noRecord}
                sortKey={hook.sortKey}
                sortOrder={hook.sortOrder}
                onSortChange={hook.changeSort}
                searchValue={hook.search}
                onSearchChange={(value) => {
                    hook.setSearch(value);
                    hook.setPage(1);
                }}
                onReset={hook.resetFilters}
                disabled={hook.isProcessing}
                disabledReason="A bulk action is currently running"
                exportOptions={exportOptions}
                filterSlot={<LinkedProgrammeFilters hook={hook} />}
                pagination={{
                    totalEntries: hook.totalRows,
                    rangeStart: hook.rangeStart,
                    rangeEnd: hook.rangeEnd,
                    rowsPerPage: hook.perPage,
                    rowsPerPageOptions,
                    onRowsPerPageChange: (value) => {
                        hook.setPerPage(value);
                        hook.setPage(1);
                    },
                    currentPage: hook.page,
                    totalPages: hook.totalPages,
                    onPageChange: hook.setPage,
                }}
            />

            <ModuleReadinessPopover
                row={modulesRow}
                pmcId={hook.id}
                anchorElement={modulesAnchor}
                onClose={() => {
                    const button = modulesAnchor;
                    setModulesRow(null);
                    setModulesAnchor(null);
                    window.setTimeout(() => button?.focus(), 0);
                }}
            />
        </>
    );
}

export default LinkedProgrammesTable;
